import { Field, Suffix, Wrapper } from './styles'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import { RefObject } from 'react'
import { PatternFormat } from 'react-number-format'
import { FormattedMessage } from 'react-intl'

export type InputType =
    | 'text'
    | 'email'
    | 'tel'
    | 'textarea'
    | 'password'
    | 'number'
interface InputProps {
    type: InputType
    id: string
    label?: string
    isSmallerLabel?: boolean
    placeholder?: string
    isDisabled?: boolean
    isRequired?: boolean
    isOptional?: boolean
    onChange?: (value: string) => void
    onBlur?: (value: string) => void
    isError?: boolean
    errorMessage?: string
    infoMessage?: string
    children?: JSX.Element
    hasNoMaxWidth?: boolean
    value?: string
    maxLength?: number
    isLargePaddingRight?: boolean
    suffix?: string
    autofill?: string
    maxWidth?: number
    mask?: string
    maskPlaceholder?: string
    inputMode?:
        | 'text'
        | 'search'
        | 'email'
        | 'tel'
        | 'url'
        | 'none'
        | 'numeric'
        | 'decimal'
    testId?: string
    refToElement?: RefObject<HTMLTextAreaElement>
    minHeight?: number
    isFocusable?: boolean
    isFullWidth?: boolean
    inputLabelFullWidth?: boolean
}

const Input = ({
    type = 'text',
    placeholder,
    id,
    label,
    isSmallerLabel = false,
    isDisabled,
    isRequired = false,
    isOptional,
    onChange,
    onBlur,
    isError,
    errorMessage,
    infoMessage,
    children,
    value,
    maxLength,
    isLargePaddingRight,
    suffix,
    autofill = 'on',
    maxWidth,
    mask,
    maskPlaceholder,
    inputMode,
    testId,
    refToElement,
    minHeight,
    isFocusable = true,
    isFullWidth = true,
    inputLabelFullWidth = false,
}: InputProps) => (
    <Box gap={4} isFullWidth={isFullWidth}>
        {!!label && (
            <Text
                as="label"
                variant={isSmallerLabel ? 'bodyExtraSmall' : 'bodySmall'}
                htmlFor={id}
                color={isError ? 'error' : 'grayscale_900'}
                hasTransition
            >
                {label}
                {isRequired && '*'}
                {isOptional && (
                    <Text as="span" variant="bodyExtraSmall" hasNoWrap>
                        {' ('}
                        <FormattedMessage
                            id="global.input.optional"
                            defaultMessage="opcjonalnie"
                        />
                        {')'}
                    </Text>
                )}
            </Text>
        )}
        <Wrapper
            maxWidth={maxWidth}
            minHeight={minHeight}
            hasNoMaxWidth={inputLabelFullWidth}
        >
            {!mask ? (
                <>
                    <Field
                        id={id}
                        type={type}
                        placeholder={placeholder}
                        disabled={!!isDisabled}
                        required={isRequired}
                        isError={isError}
                        onChange={
                            onChange &&
                            ((event: { target: { value: string } }) =>
                                onChange(event.target.value))
                        }
                        onBlur={
                            onBlur &&
                            ((event: { target: { value: string } }) =>
                                onBlur(event.target.value))
                        }
                        as={type === 'textarea' ? 'textarea' : 'input'}
                        value={value}
                        maxLength={maxLength}
                        isLargePaddingRight={isLargePaddingRight}
                        autoComplete={autofill}
                        data-testid={testId}
                        ref={refToElement ? refToElement : null}
                        tabIndex={isFocusable ? 0 : -1}
                    ></Field>
                    {!!suffix && (
                        <Suffix variant="body" color="primary_500">
                            {suffix}
                        </Suffix>
                    )}
                    {children}
                </>
            ) : (
                <>
                    <PatternFormat
                        value={value}
                        onValueChange={
                            onChange && ((values) => onChange(values.value))
                        }
                        onBlur={onBlur && (() => onBlur(id))}
                        format={mask}
                        mask={maskPlaceholder}
                        allowEmptyFormatting
                        id={id}
                        autoComplete={autofill}
                        placeholder={placeholder}
                        required={isRequired}
                        isLargePaddingRight={isLargePaddingRight}
                        maxLength={maxLength}
                        isError={isError}
                        inputMode={inputMode || 'text'}
                        customInput={Field}
                        data-testid={testId}
                        tabIndex={isFocusable ? 0 : -1}
                    />
                    {children}
                </>
            )}
        </Wrapper>

        {!!infoMessage && (
            <Wrapper maxWidth={maxWidth}>
                <Text
                    variant="bodyExtraSmall"
                    color={'grayscale_700'}
                    hasTransition
                >
                    {infoMessage}
                </Text>
            </Wrapper>
        )}

        {isError && !!errorMessage && (
            <Wrapper maxWidth={maxWidth}>
                <Text variant="bodyExtraSmall" color={'error'} hasTransition>
                    {errorMessage}
                </Text>
            </Wrapper>
        )}
    </Box>
)

export default Input
